import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SessionQuery } from '../state/session/session.query';

// Types from API models
export interface OrganizationSettings {
	[key: string]: unknown;
}

export interface PublicOrganization {
	id?: string;
	name: string;
	slug: string;
	settings: OrganizationSettings;
	created: string;
}

export interface PublicBrand {
	id?: string;
	organizationId?: string;
	name: string;
	slug: string;
	created: string;
	organization?: PublicOrganization;
	kits?: PublicKit[];
}

export interface KitPreviewLogo {
	path: string;
	name: string;
}

export interface KitPreviewColor {
	value: string;
	name: string;
}

export interface KitPreview {
	logo: KitPreviewLogo | null;
	colors: KitPreviewColor[];
}

export interface PublicKit {
	id?: string;
	organizationId?: string;
	brandId?: string;
	name: string;
	slug: string;
	description?: string;
	remoteScopes?: string[];
	created: string;
	organization?: PublicOrganization;
	brand?: PublicBrand;
	preview?: KitPreview;
}

export interface ResponseEnvelope<T> {
	status: 'success' | 'failure' | 'error';
	message?: string;
	data?: T;
}

// Paginated response from external API
export interface PaginatedResponse<T> {
	results: T[];
	pagination?: {
		page: number;
		perPage: number;
		total: number;
	};
}

/**
 * Extract array from response data that might be:
 * - Direct array: [...]
 * - Paginated object: { results: [...], pagination: {...} }
 * - Grouped paginated object: { results: { color: [...], media: [...], font: [...] }, ... }
 */
function extractArray<T>(data: T[] | PaginatedResponse<T> | undefined): T[] {
	if (!data) return [];
	if (Array.isArray(data)) return data;
	if (typeof data === 'object' && 'results' in data) {
		// Check if results is an array (standard pagination)
		if (Array.isArray(data.results)) {
			return data.results;
		}
		// Check if results is a grouped object (assets grouped by type)
		if (typeof data.results === 'object' && data.results !== null) {
			const grouped = data.results as Record<string, T[]>;
			return Object.values(grouped).flat();
		}
	}
	return [];
}

// DTOs
export interface BrandAddDto {
	name: string;
}

export interface BrandFindDto {
	id?: string;
	name?: string;
}

export interface KitAddDto {
	name: string;
	description?: string;
	remoteScopes?: string[];
}

export interface KitUpdateDto {
	name?: string;
	description?: string;
	remoteScopes?: string[];
}

export interface KitFindDto {
	id?: string;
	slug?: string;
	name?: string;
	remoteScopes?: string[];
}

// Asset types
export type AssetType = 'media' | 'font' | 'color' | 'variable-map' | 'knowledge-base';

export interface MediaData {
	path?: string;
	metadata?: {
		width?: number;
		height?: number;
		size?: number;
		mimeType?: string;
	};
}

export interface FontData {
	path?: string;
	metadata?: {
		family?: string;
		weight?: number;
		style?: 'normal' | 'italic';
	};
}

export interface ColorData {
	value: string; // hex color
}

export interface VariableMapData {
	value: Record<string, unknown>;
}

export interface KnowledgeBaseData {
	value: string;
	subtype?: string;
	personaName?: string;
}

export type AssetData = MediaData | FontData | ColorData | VariableMapData | KnowledgeBaseData;

export interface PublicAsset {
	id?: string;
	type: AssetType;
	name: string;
	description?: string;
	tag?: string; // Used to categorize assets (e.g., 'logo' for logo media assets)
	data: AssetData;
	sortIdx?: number;
	kitId?: string;
	brandId?: string;
	organizationId?: string;
}

export interface AssetAddDto {
	type: AssetType;
	name: string;
	description?: string;
	tag?: string;
	data?: AssetData;
}

export interface AssetUpdateDto {
	name?: string;
	description?: string;
	tag?: string;
	data?: AssetData;
}

export interface AssetFindDto {
	id?: string;
	types?: AssetType[];
	tag?: string;
}

@Injectable({ providedIn: 'root' })
export class BrandKitService {
	private readonly baseUrl = `${environment.apiUrl}/integrations/vml-brandkit`;

	constructor(
		private readonly http: HttpClient,
		private readonly sessionQuery: SessionQuery
	) {}

	// === BRAND METHODS (using simplified endpoints) ===

	findBrands(filter?: Partial<BrandFindDto>): Observable<PublicBrand[]> {
		return this.http
			.post<ResponseEnvelope<PublicBrand[] | PaginatedResponse<PublicBrand>>>(
				`${this.baseUrl}/brands/find`,
				filter || {}
			)
			.pipe(map(res => extractArray(res.data)));
	}

	createBrand(brand: BrandAddDto): Observable<PublicBrand> {
		return this.http
			.post<ResponseEnvelope<PublicBrand>>(
				`${this.baseUrl}/brands`,
				brand
			)
			.pipe(map(res => res.data));
	}

	// === KIT METHODS (using simplified endpoints) ===

	findKits(brandIdOrSlug: string, filter?: Partial<KitFindDto>): Observable<PublicKit[]> {
		return this.http
			.post<ResponseEnvelope<PublicKit[] | PaginatedResponse<PublicKit>>>(
				`${this.baseUrl}/brands/${brandIdOrSlug}/kits/find`,
				filter || {}
			)
			.pipe(map(res => extractArray(res.data)));
	}

	/**
	 * Find all kits across all brands using wildcard endpoint.
	 * - Admins: Use allRemoteScopes=true to get all kits
	 * - Regular users: Pass their business IDs as remoteScopes filter
	 */
	findAllKits(filter?: Partial<KitFindDto>): Observable<PublicKit[]> {
		const isAdmin = this.sessionQuery.isAtLeastAdmin();
		const profile = this.sessionQuery.getProfile();
		const userBusinessIds = profile?.businesses?.map(b => b.id) || [];

		// Build query params
		let params = new HttpParams().set('getPreviews', 'true');

		if (isAdmin) {
			// Admins get all kits without scope filtering
			params = params.set('allRemoteScopes', 'true');
		}

		// Build request body
		const body: KitFindDto = {
			...filter,
			// Regular users filter by their business scopes
			...(!isAdmin && userBusinessIds.length > 0 ? { remoteScopes: userBusinessIds } : {})
		};

		return this.http
			.post<ResponseEnvelope<PublicKit[] | PaginatedResponse<PublicKit>>>(
				`${this.baseUrl}/brands/*/kits/find`,
				body,
				{ params }
			)
			.pipe(map(res => extractArray(res.data)));
	}

	/**
	 * Create kit with user-selected business IDs as remoteScopes.
	 * Validates that user has access to the selected business IDs.
	 */
	createKit(brandIdOrSlug: string, kit: KitAddDto): Observable<PublicKit> {
		const validatedScopes = this.validateBusinessScopes(kit.remoteScopes || []);

		const kitWithScopes: KitAddDto = {
			...kit,
			remoteScopes: validatedScopes
		};

		return this.http
			.post<ResponseEnvelope<PublicKit>>(
				`${this.baseUrl}/brands/${brandIdOrSlug}/kits`,
				kitWithScopes
			)
			.pipe(map(res => res.data));
	}

	/**
	 * Update kit with user-selected business IDs as remoteScopes.
	 * Validates that user has access to the selected business IDs.
	 */
	updateKit(brandIdOrSlug: string, kitSlug: string, kit: KitUpdateDto): Observable<PublicKit> {
		const validatedScopes = kit.remoteScopes !== undefined
			? this.validateBusinessScopes(kit.remoteScopes)
			: undefined;

		const kitWithScopes: KitUpdateDto = {
			...kit,
			...(validatedScopes !== undefined ? { remoteScopes: validatedScopes } : {})
		};

		return this.http
			.put<ResponseEnvelope<PublicKit>>(
				`${this.baseUrl}/brands/${brandIdOrSlug}/kits/${kitSlug}`,
				kitWithScopes
			)
			.pipe(map(res => res.data));
	}

	/**
	 * Validate that user has access to the selected business IDs.
	 * - Admins can assign any business IDs
	 * - Regular users can only assign their own business IDs
	 */
	private validateBusinessScopes(selectedBusinessIds: string[]): string[] {
		const isAdmin = this.sessionQuery.isAtLeastAdmin();

		// Admins can assign any businesses
		if (isAdmin) {
			return selectedBusinessIds;
		}

		// Regular users: filter to only businesses they belong to
		const profile = this.sessionQuery.getProfile();
		const userBusinessIds = new Set(profile?.businesses?.map(b => b.id) || []);

		return selectedBusinessIds.filter(id => userBusinessIds.has(id));
	}

	deleteKit(brandIdOrSlug: string, kitSlug: string): Observable<void> {
		return this.http
			.delete<ResponseEnvelope<null>>(
				`${this.baseUrl}/brands/${brandIdOrSlug}/kits/${kitSlug}`
			)
			.pipe(map(() => void 0));
	}

	/**
	 * Get a single kit by brand slug and kit slug.
	 */
	getKit(brandIdOrSlug: string, kitSlug: string): Observable<PublicKit | null> {
		return this.http
			.get<ResponseEnvelope<PublicKit>>(
				`${this.baseUrl}/brands/${brandIdOrSlug}/kits/${kitSlug}`
			)
			.pipe(map(res => res.data || null));
	}

	/**
	 * Get a single brand by slug.
	 */
	getBrand(brandIdOrSlug: string): Observable<PublicBrand | null> {
		return this.http
			.get<ResponseEnvelope<PublicBrand>>(
				`${this.baseUrl}/brands/${brandIdOrSlug}`
			)
			.pipe(map(res => res.data || null));
	}

	// === ASSET METHODS ===

	/**
	 * Find assets in a brand kit.
	 */
	findAssets(brandIdOrSlug: string, kitSlug: string, filter?: Partial<AssetFindDto>): Observable<PublicAsset[]> {
		return this.http
			.post<ResponseEnvelope<PublicAsset[] | PaginatedResponse<PublicAsset>>>(
				`${this.baseUrl}/brands/${brandIdOrSlug}/kits/${kitSlug}/assets/find`,
				filter || {}
			)
			.pipe(map(res => extractArray(res.data)));
	}

	/**
	 * Add an asset to a brand kit.
	 * For media/font assets with files, use addAssetWithFile instead.
	 */
	addAsset(brandIdOrSlug: string, kitSlug: string, asset: AssetAddDto): Observable<PublicAsset> {
		return this.http
			.post<ResponseEnvelope<PublicAsset>>(`${this.baseUrl}/brands/${brandIdOrSlug}/kits/${kitSlug}/assets`, asset)
			.pipe(map(res => res.data));
	}

	/**
	 * Add an asset with a file upload (for media/font assets).
	 */
	addAssetWithFile(brandIdOrSlug: string, kitSlug: string, asset: AssetAddDto, file: File): Observable<PublicAsset> {
		const formData = new FormData();
		formData.append('file', file);
		formData.append('type', asset.type);
		formData.append('name', asset.name);
		if (asset.description) {
			formData.append('description', asset.description);
		}
		if (asset.tag) {
			formData.append('tag', asset.tag);
		}
		if (asset.data) {
			formData.append('data', JSON.stringify(asset.data));
		}

		return this.http
			.post<ResponseEnvelope<PublicAsset>>(`${this.baseUrl}/brands/${brandIdOrSlug}/kits/${kitSlug}/assets`, formData)
			.pipe(map(res => res.data));
	}

	/**
	 * Update an asset.
	 */
	updateAsset(brandIdOrSlug: string, kitSlug: string, assetId: string, updates: AssetUpdateDto): Observable<PublicAsset> {
		return this.http
			.put<ResponseEnvelope<PublicAsset>>(
				`${this.baseUrl}/brands/${brandIdOrSlug}/kits/${kitSlug}/assets/${assetId}`,
				updates
			)
			.pipe(map(res => res.data));
	}

	/**
	 * Update an asset with optional file replacement (for media/font assets).
	 */
	updateAssetWithFile(
		brandIdOrSlug: string,
		kitSlug: string,
		assetId: string,
		updates: Partial<AssetUpdateDto>,
		file?: File
	): Observable<PublicAsset> {
		const formData = new FormData();

		if (file) {
			formData.append('file', file);
		}
		if (updates.name) {
			formData.append('name', updates.name);
		}
		if (updates.description !== undefined) {
			formData.append('description', updates.description || '');
		}
		if (updates.tag) {
			formData.append('tag', updates.tag);
		}
		if (updates.data) {
			formData.append('data', JSON.stringify(updates.data));
		}

		return this.http
			.put<ResponseEnvelope<PublicAsset>>(
				`${this.baseUrl}/brands/${brandIdOrSlug}/kits/${kitSlug}/assets/${assetId}`,
				formData
			)
			.pipe(map(res => res.data));
	}

	/**
	 * Delete an asset.
	 */
	deleteAsset(brandIdOrSlug: string, kitSlug: string, assetId: string): Observable<void> {
		return this.http
			.delete<ResponseEnvelope<null>>(`${this.baseUrl}/brands/${brandIdOrSlug}/kits/${kitSlug}/assets/${assetId}`)
			.pipe(map(() => void 0));
	}
}
